import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useEscape } from '@core/shortcuts'

import {
  PASSOS,
  type PassoTour,
  marcarTourVisto,
  passosDisponiveis,
  proximoIndice,
} from '@core/layout/tour/steps'
import { posicaoDoBalao } from '@core/layout/tour/posicionar'
import { assinatura, criarVigia } from '@core/layout/tour/vigia'

interface TourProps {
  aoFechar: () => void
}

/** O guia de dois minutos.
 *
 *  Desenha um balão junto ao elemento de cada passo e escurece o
 *  resto, deixando um buraco por cima do elemento.
 *
 *  A posição é escrita directamente no elemento, sem passar pelo
 *  estado: guardá-la em estado obrigava a um render por cada scroll ou
 *  redimensionamento. É o mesmo que já se faz no RibbonMenu. O que
 *  faltava era recalculá-la quando o ecrã mexe, e é isso que a vigia
 *  por quadro faz, com a decisão de "mexeu?" em `vigia.ts`. */
export default function Tour({ aoFechar }: TourProps) {
  const [passos] = useState<PassoTour[]>(() =>
    passosDisponiveis((alvo) =>
      Boolean(document.querySelector(`[data-tour="${alvo}"]`)),
    ),
  )
  const [i, setI] = useState(0)
  const balaoRef = useRef<HTMLDivElement>(null)
  const realceRef = useRef<HTMLDivElement>(null)

  const passo = passos[i]

  useLayoutEffect(() => {
    if (!passo) return

    const colocar = () => {
      const balao = balaoRef.current
      const realce = realceRef.current
      if (!balao) return

      const elemento = passo.semAlvo
        ? null
        : document.querySelector(`[data-tour="${passo.alvo}"]`)

      if (!elemento) {
        // Passo sem alvo: balão ao centro e sem realce.
        balao.style.left = '50%'
        balao.style.top = '50%'
        balao.style.transform = 'translate(-50%, -50%)'
        if (realce) realce.style.display = 'none'
        return
      }

      const r = elemento.getBoundingClientRect()
      if (realce) {
        realce.style.display = 'block'
        realce.style.left = `${r.left - 4}px`
        realce.style.top = `${r.top - 4}px`
        realce.style.width = `${r.width + 8}px`
        realce.style.height = `${r.height + 8}px`
      }
      balao.style.transform = 'none'
      // A conta vive em `posicionar.ts`, que é puro e tem testes. Aqui
      // fazia-se à mão e limitava-se só a horizontal: o passo da régua,
      // que é alta como o ecrã, punha o balão 153 px abaixo dele.
      const pos = posicaoDoBalao(
        r,
        { width: balao.offsetWidth, height: balao.offsetHeight },
        { largura: window.innerWidth, altura: window.innerHeight },
        passo.lado,
      )
      balao.style.left = `${pos.esquerda}px`
      balao.style.top = `${pos.topo}px`
    }

    // A posição era medida uma vez por passo e ficava assim para
    // sempre. Se a janela mudasse de tamanho, se um painel abrisse ao
    // lado ou se uma lista deslizasse, o balão e o recorte ficavam onde
    // o elemento ESTAVA: recorte ao lado do botão, balão meio fora do
    // ecrã, sem nada de errado na conta que os põe lá.
    //
    // Tentei primeiro com `resize`, `scroll` e um ResizeObserver no
    // body, e não chega: abrir um painel ao lado move o alvo sem mexer
    // no tamanho do body nem disparar nenhum dos dois eventos. Ficava
    // metade do defeito por corrigir.
    //
    // Por isso a vigia é por quadro. Lê o rectângulo do alvo, compara
    // com o anterior e só escreve quando alguma coisa mexeu. É uma
    // leitura por quadro durante um guia de dois minutos, e apanha
    // tudo: redimensionar, abrir painéis, deslizar, animações e zoom.
    // Com o separador escondido o browser suspende os quadros, que é o
    // que se quer: ninguém está a olhar, e ao voltar o primeiro quadro
    // repõe tudo no sítio.
    const vigia = criarVigia(() => {
      const elemento = passo.semAlvo
        ? null
        : document.querySelector(`[data-tour="${passo.alvo}"]`)
      return assinatura(
        elemento?.getBoundingClientRect() ?? null,
        { largura: window.innerWidth, altura: window.innerHeight },
        balaoRef.current?.offsetHeight ?? 0,
      )
    }, colocar)

    let quadro = 0
    const porQuadro = () => {
      vigia()
      quadro = requestAnimationFrame(porQuadro)
    }
    porQuadro()

    return () => cancelAnimationFrame(quadro)
  }, [passo])

  const terminar = () => {
    marcarTourVisto()
    aoFechar()
  }
  // O despachante de atalhos fica com o Escape antes de qualquer
  // listener da janela; registar aqui é o que faz o Escape fechar a
  // visita em vez de limpar a selecção.
  useEscape(!!passo, terminar)

  const avancar = () => {
    const seguinte = proximoIndice(i, passos.length)
    if (seguinte === null) terminar()
    else setI(seguinte)
  }

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        terminar()
      }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        avancar()
      }
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  })

  if (!passo) return null

  return (
    // Entre os diálogos e os avisos, ver core/layout/camadas.ts.
    <div className="fixed inset-0 z-[1050]">
      {/* Apanha o clique fora, para fechar, como qualquer sobreposição
          desta aplicação.

          Escurecia o ecrã inteiro, e é por isso que o recorte nunca
          chegou a recortar nada: esta camada passava TAMBÉM por cima do
          elemento realçado, e o anel do realce acrescentava mais uma
          por fora. O que se via era o ecrã todo escuro, com o botão que
          o guia estava a apontar tão escuro como o resto.

          Agora quem escurece é só o anel do realce, que deixa o buraco
          onde é preciso. Nos passos sem alvo não há anel, e aí é esta
          camada que escurece. */}
      <div
        className={`absolute inset-0 ${passo.semAlvo ? 'bg-black/50' : ''}`}
        onClick={terminar}
      />
      <div
        ref={realceRef}
        className="pointer-events-none absolute rounded border-2 border-[var(--color-accent)] shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
        style={{ display: 'none' }}
      />
      <div
        ref={balaoRef}
        role="dialog"
        aria-label="Guia de dois minutos"
        className="absolute w-80 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-2xl"
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <h2 className="text-sm font-medium text-[var(--color-text)]">
            {passo.titulo}
          </h2>
          <button
            type="button"
            onClick={terminar}
            aria-label="Fechar o guia"
            className="shrink-0 rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        <p className="text-xs leading-relaxed text-[var(--color-text-dim)]">
          {passo.corpo}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] text-[var(--color-text-dim)]">
            {i + 1} de {passos.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={terminar}
              className="rounded px-2 py-1 text-[11px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
            >
              Sair
            </button>
            <button
              type="button"
              onClick={avancar}
              className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-3 py-1 text-[11px] font-medium text-[var(--color-text)] hover:bg-[var(--color-accent-hover)]"
            >
              {i === passos.length - 1 ? 'Terminar' : 'Seguinte'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { PASSOS }
